import { useMemo, useState } from "react";
import { Link, Navigate, useNavigate, useSearchParams } from "react-router-dom";
import { partnerLogin, partnerRegister } from "../../api/partnerAuthApi";
import { usePartnerAuth } from "../../auth/PartnerAuthProvider";
import { usePartnerPortalBase } from "../../components/partner/PartnerPortalBaseProvider";
import { useLocale } from "../../intl";
import { resolveSafeReturnTo } from "../../utils/automap/legalDocumentRender";
import { isCasaMapaPartnerAccountIntent } from "../../utils/casamapaPartnerPortalPaths";
import "../../styles/partnerCasaMapaAuth.css";

const COPY = "realEstate.casaMapa.admin.partnerLogin";
const BENEFITS = ["profile", "inbox", "market", "listings", "directory", "tools"];

function parseMode(value) {
  return value === "register" ? "register" : "signin";
}

function parseKind(intent, fallback = "agency") {
  return intent === "vendor" ? "vendor" : fallback === "vendor" ? "vendor" : "agency";
}

export default function CasaMapaPartnerAuthPage() {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const { isAuthenticated, refresh } = usePartnerAuth();
  const { dashboardPath, portalLabel } = usePartnerPortalBase();
  const { t } = useLocale();

  const intent = searchParams.get("intent");
  const mode = parseMode(searchParams.get("mode"));
  const returnTo = resolveSafeReturnTo(searchParams.get("returnTo"), dashboardPath);
  const isPartnerIntent = isCasaMapaPartnerAccountIntent(intent);

  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [firstName, setFirstName] = useState("");
  const [lastName, setLastName] = useState("");
  const [businessName, setBusinessName] = useState("");
  const [phone, setPhone] = useState("");
  const [websiteUrl, setWebsiteUrl] = useState("");
  const [affiliationName, setAffiliationName] = useState("");
  const [notes, setNotes] = useState("");
  const [partnerKind, setPartnerKind] = useState(parseKind(intent));
  const [errorMessage, setErrorMessage] = useState("");
  const [submitting, setSubmitting] = useState(false);

  const intro = useMemo(() => {
    if (isPartnerIntent) {
      return t(`${COPY}.subtitle.${intent}`);
    }
    return t(`${COPY}.heroIntro`);
  }, [intent, isPartnerIntent, t]);

  if (isAuthenticated) {
    return <Navigate to={returnTo} replace />;
  }

  const setMode = (nextMode) => {
    const next = new URLSearchParams(searchParams);
    if (nextMode === "register") next.set("mode", "register");
    else next.delete("mode");
    setSearchParams(next, { replace: true });
    setErrorMessage("");
  };

  const handleSignIn = async (event) => {
    event.preventDefault();
    setErrorMessage("");
    setSubmitting(true);
    try {
      await partnerLogin(email, password);
      await refresh();
      navigate(returnTo);
    } catch (error) {
      setErrorMessage(error.message || t(`${COPY}.signInFailed`));
    } finally {
      setSubmitting(false);
    }
  };

  const handleRegister = async (event) => {
    event.preventDefault();
    setErrorMessage("");
    setSubmitting(true);
    try {
      await partnerRegister({
        email,
        password,
        firstName,
        lastName,
        businessName,
        phone,
        websiteUrl,
        affiliationName,
        notes,
        partnerKind,
      });
      await refresh();
      navigate(returnTo);
    } catch (error) {
      setErrorMessage(error.message || t(`${COPY}.registerFailed`));
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="cm-partner-auth">
      <div className="cm-partner-auth__shell">
        <header className="cm-partner-auth__brand">
          <span className="cm-partner-auth__mark" aria-hidden="true">
            CM
          </span>
          <div>
            <strong>{portalLabel}</strong>
            <small>{t(`${COPY}.brandHint`)}</small>
          </div>
        </header>

        <div className="cm-partner-auth__grid">
          <section className="cm-partner-auth__benefits" aria-labelledby="cm-partner-auth-title">
            <p className="cm-partner-auth__eyebrow">{t(`${COPY}.eyebrow`)}</p>
            <h1 id="cm-partner-auth-title">{t(`${COPY}.heroTitle`)}</h1>
            <p className="cm-partner-auth__intro">{intro}</p>

            <ul className="cm-partner-auth__benefit-list">
              {BENEFITS.map((key) => (
                <li key={key}>
                  <strong>{t(`${COPY}.benefits.${key}.title`)}</strong>
                  <span>{t(`${COPY}.benefits.${key}.body`)}</span>
                </li>
              ))}
            </ul>

            <aside className="cm-partner-auth__review">
              <p className="cm-partner-auth__review-label">{t(`${COPY}.review.label`)}</p>
              <p>{t(`${COPY}.review.body`)}</p>
            </aside>
          </section>

          <section className="cm-partner-auth__panel" aria-labelledby="cm-partner-auth-form-title">
            <div className="cm-partner-auth__tabs" role="tablist" aria-label={t(`${COPY}.tabsLabel`)}>
              <button
                type="button"
                role="tab"
                aria-selected={mode === "signin"}
                className={mode === "signin" ? "is-active" : ""}
                onClick={() => setMode("signin")}
              >
                {t(`${COPY}.tabs.signin`)}
              </button>
              <button
                type="button"
                role="tab"
                aria-selected={mode === "register"}
                className={mode === "register" ? "is-active" : ""}
                onClick={() => setMode("register")}
              >
                {t(`${COPY}.tabs.register`)}
              </button>
            </div>

            {mode === "signin" ? (
              <>
                <h2 id="cm-partner-auth-form-title">{t(`${COPY}.signInTitle`)}</h2>
                <p className="cm-partner-auth__panel-copy">{t(`${COPY}.signInBody`)}</p>
                <form onSubmit={handleSignIn} className="cm-partner-auth__form">
                  <label>
                    <span>{t(`${COPY}.fields.email`)}</span>
                    <input
                      type="email"
                      value={email}
                      onChange={(event) => setEmail(event.target.value)}
                      required
                      autoComplete="username"
                    />
                  </label>
                  <label>
                    <span>{t(`${COPY}.fields.password`)}</span>
                    <input
                      type="password"
                      value={password}
                      onChange={(event) => setPassword(event.target.value)}
                      required
                      autoComplete="current-password"
                    />
                  </label>
                  {errorMessage ? (
                    <p className="cm-partner-auth__error" role="alert">
                      {errorMessage}
                    </p>
                  ) : null}
                  <button type="submit" className="cm-partner-auth__submit" disabled={submitting}>
                    {submitting ? t(`${COPY}.signingIn`) : t(`${COPY}.signInCta`)}
                  </button>
                </form>
                <p className="cm-partner-auth__switch">
                  {t(`${COPY}.needAccount`)}{" "}
                  <button type="button" onClick={() => setMode("register")}>
                    {t(`${COPY}.createAccountLink`)}
                  </button>
                </p>
              </>
            ) : (
              <>
                <h2 id="cm-partner-auth-form-title">{t(`${COPY}.registerTitle`)}</h2>
                <p className="cm-partner-auth__panel-copy">{t(`${COPY}.registerBody`)}</p>
                <form onSubmit={handleRegister} className="cm-partner-auth__form">
                  <fieldset className="cm-partner-auth__kinds">
                    <legend>{t(`${COPY}.fields.kind`)}</legend>
                    {["agency", "vendor"].map((kind) => (
                      <label key={kind} className={partnerKind === kind ? "is-active" : ""}>
                        <input
                          type="radio"
                          name="partnerKind"
                          value={kind}
                          checked={partnerKind === kind}
                          onChange={() => setPartnerKind(kind)}
                        />
                        <strong>{t(`${COPY}.kinds.${kind}.title`)}</strong>
                        <span>{t(`${COPY}.kinds.${kind}.body`)}</span>
                      </label>
                    ))}
                  </fieldset>
                  <div className="cm-partner-auth__row">
                    <label>
                      <span>{t(`${COPY}.fields.firstName`)}</span>
                      <input
                        value={firstName}
                        onChange={(event) => setFirstName(event.target.value)}
                        required
                        autoComplete="given-name"
                      />
                    </label>
                    <label>
                      <span>{t(`${COPY}.fields.lastName`)}</span>
                      <input
                        value={lastName}
                        onChange={(event) => setLastName(event.target.value)}
                        required
                        autoComplete="family-name"
                      />
                    </label>
                  </div>
                  <label>
                    <span>{t(`${COPY}.fields.businessName`)}</span>
                    <input
                      value={businessName}
                      onChange={(event) => setBusinessName(event.target.value)}
                      required
                      autoComplete="organization"
                    />
                  </label>
                  <label>
                    <span>{t(`${COPY}.fields.email`)}</span>
                    <input
                      type="email"
                      value={email}
                      onChange={(event) => setEmail(event.target.value)}
                      required
                      autoComplete="email"
                    />
                  </label>
                  <label>
                    <span>{t(`${COPY}.fields.password`)}</span>
                    <input
                      type="password"
                      value={password}
                      onChange={(event) => setPassword(event.target.value)}
                      required
                      minLength={10}
                      autoComplete="new-password"
                    />
                  </label>
                  <div className="cm-partner-auth__row">
                    <label>
                      <span>{t(`${COPY}.fields.phone`)}</span>
                      <input
                        value={phone}
                        onChange={(event) => setPhone(event.target.value)}
                        autoComplete="tel"
                      />
                    </label>
                    <label>
                      <span>{t(`${COPY}.fields.website`)}</span>
                      <input
                        value={websiteUrl}
                        onChange={(event) => setWebsiteUrl(event.target.value)}
                        placeholder="https://"
                        autoComplete="url"
                      />
                    </label>
                  </div>
                  <label>
                    <span>{t(`${COPY}.fields.affiliation`)}</span>
                    <input
                      value={affiliationName}
                      onChange={(event) => setAffiliationName(event.target.value)}
                      placeholder={t(`${COPY}.fields.affiliationHint`)}
                    />
                  </label>
                  <label>
                    <span>{t(`${COPY}.fields.notes`)}</span>
                    <textarea
                      value={notes}
                      rows={3}
                      onChange={(event) => setNotes(event.target.value)}
                    />
                  </label>
                  <p className="cm-partner-auth__fineprint">{t(`${COPY}.registerFineprint`)}</p>
                  {errorMessage ? (
                    <p className="cm-partner-auth__error" role="alert">
                      {errorMessage}
                    </p>
                  ) : null}
                  <button type="submit" className="cm-partner-auth__submit" disabled={submitting}>
                    {submitting ? t(`${COPY}.registering`) : t(`${COPY}.registerCta`)}
                  </button>
                </form>
                <p className="cm-partner-auth__switch">
                  {t(`${COPY}.haveAccount`)}{" "}
                  <button type="button" onClick={() => setMode("signin")}>
                    {t(`${COPY}.signInLink`)}
                  </button>
                </p>
              </>
            )}

            <p className="cm-partner-auth__back">
              <Link to="/v/casamapa">{t(`${COPY}.backToMap`)}</Link>
            </p>
          </section>
        </div>
      </div>
    </div>
  );
}
